import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { KizenTypography } from '__app/typography';
import { Button } from '@kizen/kds/Button';
import Input from '__components/Kizen/Input';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import {
  StyledButtonsWrapper,
  StyledDetails,
  StyledPageWrapper,
  StyledPaginationWrapper,
  StyledSearchInput,
  StyledTableScrollContainer,
  StyledTableToolbar,
  StyledTableWrapper,
} from 'pages/KnowledgeSettings/styles';
import BasicTable from '__components/Tables/Basic';
import { TRow } from '__components/Kizen/Table';
import TablePagination from 'components/Tables/Big/TablePagination';
import {
  DEFAULT_PAGINATION_SETTINGS,
  FILES_TABLE_ACTIONS,
} from 'pages/KnowledgeSettings/constants';
import { useColumns } from './columns';
import { downloadFile } from 'utility/DownloadFile';
import { knowledgeSourceSearchQuery } from 'query/knowledge';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import KnowledgeService from 'services/KnowledgeService';
import type { KnowledgeSource } from '@kizen/api/knowledge';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import {
  AddKnowledgeSourceModal,
  type OnConfirmAddFileParams,
} from './AddKnowledgeSourceModal';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import useModal from '__components/Modals/useModal';
import BasicModal from '__components/Modals/presets/BasicModal';
import Loader from '__components/Kizen/Loader';
import { usePageConfig } from 'pages/KnowledgeSettings/hooks/usePageConfig';
import { useCatalogPermission } from 'pages/KnowledgeSettings/hooks/useCatalogPermission';

export const CatalogFileSources = () => {
  const { t } = useTranslation();
  const { catalogId } = useParams<{ catalogId: string }>();
  const [showToast, toastVariant] = useToast();

  const { canWrite } = useCatalogPermission(catalogId);

  const {
    paginationSettings: { page, pageSize, ordering, search },
    actualSearch,
    handleChangePage,
    handleChangePageSize,
    handleChangeSearch,
    headData,
    pageIsReady,
  } = usePageConfig({
    pageKey: 'knowledge_files_list',
    defaultConfig: DEFAULT_PAGINATION_SETTINGS,
  });

  const queryClient = useQueryClient();
  const [showAddFileModal, setShowAddFileModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<KnowledgeSource | null>(
    null
  );

  const queryOptions = useMemo(
    () =>
      knowledgeSourceSearchQuery({
        params: {
          page,
          page_size: pageSize,
          ordering,
          search,
          knowledge_type: 'business',
          catalog_id: catalogId,
        },
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: pageIsReady,
      }),
    [pageSize, page, ordering, search, catalogId, pageIsReady]
  );

  const { data, isLoading, isFetching } = useQuery(queryOptions);
  const loading = isLoading || isFetching;

  const onRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
  }, [queryClient, queryOptions.queryKey]);

  const onSelectAction = useCallback(
    async (
      { value }: { value: keyof typeof FILES_TABLE_ACTIONS },
      file: KnowledgeSource
    ) => {
      if (value === FILES_TABLE_ACTIONS.delete) {
        setFileToDelete(file);
      }
      if (value === FILES_TABLE_ACTIONS.download) {
        try {
          const blob = await KnowledgeService.downloadKnowledgeSource(file.id, {
            params: { catalog_id: catalogId, disposition: 'attachment' },
          });
          const objectUrl = URL.createObjectURL(blob);
          downloadFile(
            objectUrl,
            file.s3_object?.name || file.description || ''
          );
          setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
        } catch (error) {
          showToast({
            message:
              getErrorMessage(getOriginalError(error)) ||
              t('Failed to download file '),
            variant: toastVariant.FAILURE,
          });
        }
      }
    },
    [catalogId, showToast, t, toastVariant]
  );

  const onDeleteConfirmed = useCallback(async () => {
    try {
      if (fileToDelete?.id) {
        await KnowledgeService.deleteKnowledgeSource(fileToDelete.id);
        onRefresh();
        showToast({
          message: t('File deleted successfully'),
          variant: toastVariant.SUCCESS,
        });
        setFileToDelete(null);
      }
    } catch (error) {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to delete Knowledge Source File'),
        variant: toastVariant.FAILURE,
      });
    }
  }, [fileToDelete?.id, onRefresh, showToast, t, toastVariant]);

  const handleAddFile = useCallback(
    async ({
      s3Object,
      description,
      knowledgeType,
    }: OnConfirmAddFileParams) => {
      try {
        await KnowledgeService.createKnowledgeSource({
          knowledge_type: knowledgeType,
          s3_object_id: s3Object.id,
          description,
          catalog_id: catalogId,
        });
        showToast({
          message: t('File added successfully'),
          variant: toastVariant.SUCCESS,
        });
        onRefresh();
        setShowAddFileModal(false);
      } catch (error) {
        showToast({
          message:
            getErrorMessage(getOriginalError(error)) ||
            t('Failed to add Knowledge Source File'),
          variant: toastVariant.FAILURE,
        });
      }
    },
    [catalogId, onRefresh, showToast, t, toastVariant]
  );

  const [details, setDetails] =
    useState<KnowledgeSource['processing_details']>(null);
  const [detailsModalProps, , { show: showDetailsModalProp }] = useModal();

  const onShowDetails = useCallback(
    (file: KnowledgeSource) => {
      showDetailsModalProp();
      setDetails(file.processing_details);
    },
    [showDetailsModalProp]
  );

  const columns = useColumns({ onSelectAction, onShowDetails, canWrite });

  return (
    <Loader loading={!pageIsReady}>
      <StyledPageWrapper>
        <StyledTableToolbar>
          <StyledSearchInput
            pill
            type={Input.INPUT_TYPES.SEARCH}
            placeholder={t('Find Files')}
            value={actualSearch}
            onChange={handleChangeSearch}
            data-qa-id="catalog-file-sources-search"
          />
          <KizenTypography as="h2" type="header">
            {t('{{count}} Knowledge Source {{instance}}', {
              count: data?.count || 0,
              instance: data?.count === 1 ? t('File') : t('Files'),
            })}
          </KizenTypography>
          <StyledButtonsWrapper>
            <Button
              leftIcon="action-refresh"
              variant="text"
              onClick={onRefresh}
              color={loading ? 'inherit' : 'secondary'}
              disabled={loading}
              data-qa-id="catalog-file-sources-refresh-button"
            />
            {canWrite ? (
              <Button
                onClick={() => setShowAddFileModal(true)}
                data-qa-id="catalog-add-file-source-button"
              >
                {t('Add File Source')}
              </Button>
            ) : null}
          </StyledButtonsWrapper>
        </StyledTableToolbar>
        <StyledTableWrapper>
          <StyledTableScrollContainer bottom top gradient={<Gradient />}>
            <BasicTable
              stickyHeader
              head={<TRow head columns={columns} data={headData} />}
              staleData={loading}
            >
              {data?.count ? null : <TRow />}
              {data?.results.map((file) => (
                <TRow key={file.id} columns={columns} data={file} />
              ))}
            </BasicTable>
          </StyledTableScrollContainer>
        </StyledTableWrapper>
        <StyledPaginationWrapper>
          <TablePagination
            page={page}
            perPage={pageSize}
            totalCount={data?.count}
            onChangePage={handleChangePage}
            onChangePerPage={handleChangePageSize}
          />
        </StyledPaginationWrapper>

        <ConfirmDeletionModal
          show={Boolean(fileToDelete)}
          onConfirm={onDeleteConfirmed}
          onHide={() => setFileToDelete(null)}
        >
          {t('This will permanently delete the file.')}
        </ConfirmDeletionModal>

        {showAddFileModal ? (
          <AddKnowledgeSourceModal
            onConfirm={handleAddFile}
            onHide={() => setShowAddFileModal(false)}
            knowledgeType="business"
            showKnowledgeTypeSelect={false}
          />
        ) : null}

        {detailsModalProps.show ? (
          <BasicModal
            {...detailsModalProps}
            heading={t('Processing Details')}
            leftBtn={null}
            buttonText={t('Close')}
            actionBtnColor="blue"
            size="medium"
          >
            <StyledDetails>
              {JSON.stringify(details || {}, null, 2)}
            </StyledDetails>
          </BasicModal>
        ) : null}
      </StyledPageWrapper>
    </Loader>
  );
};
